import cn from 'classnames';
import { CurrentRefinementsConnectorParamsRefinement } from 'instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements';
import React from 'react';
import {
  CurrentRefinementsProps,
  useCurrentRefinements,
} from 'react-instantsearch';
import { useTranslation } from 'next-i18next';

import { colours } from '../../Sidebar/ColourRefinement/utils';
import { CLOTHING_SIZE_GROUP_DELIMITER } from '../../Sidebar/ClothingSizeRefinement';

import RatingStars from '@components/product/ProductRatingStars';
import { formatPrice } from '@hooks/usePrice';
import { isModifierClick } from '@utils/algolia/isModifierClick';
import { AlgoliaFacets } from '@utils/algolia/knownFacetNames';
import { isEffectivePriceAttribute } from '@utils/algolia/priceContext';

import styles from './CurrentRefinements.module.scss';

function RefinementLabel({
  refinement,
  classNames,
  refine,
  locale,
}: {
  refinement: CurrentRefinementsConnectorParamsRefinement;
  classNames: CurrentRefinementsProps['classNames'];
  refine: (refinement: CurrentRefinementsConnectorParamsRefinement) => void;
  locale?: string;
}) {
  const { t } = useTranslation('searchResults');
  const { attribute, label, value } = refinement;

  const isColour = attribute === AlgoliaFacets.ColourVariants;
  const isPrice =
    attribute === AlgoliaFacets.Price || isEffectivePriceAttribute(attribute);
  const isRating = attribute === AlgoliaFacets.ReviewScoreFlattened;
  const isPromotion = attribute === AlgoliaFacets.Promotions;
  const isClothingSize = (
    [
      AlgoliaFacets.ClothingSizeKids,
      AlgoliaFacets.ClothingSizeMens,
      AlgoliaFacets.ClothingSizeWomens,
    ] as string[]
  ).includes(attribute);

  const isProductLine = attribute === AlgoliaFacets.Collection;
  const cleanLabelFori18n = label.toLocaleLowerCase().replace(/\s+/g, '-');

  return (
    <span
      className={cn(
        'ais-CurrentRefinements-categoryLabel',
        classNames?.categoryLabel,
        styles.currentRefinementsLabel,
        { [styles.isProductLine]: isProductLine }
      )}
    >
      {isColour ? (
        <>
          <span
            className={styles.swatch}
            style={{
              background: colours[value]?.colour ?? 'var(--color-greige)',
            }}
          ></span>
          {label}
        </>
      ) : isRating ? (
        <RatingStars rating={parseFloat(label)} />
      ) : isPrice ? (
        <>
          {label.replace(
            value.toString(),
            formatPrice({
              amount: typeof value === 'number' ? value : parseFloat(value),
              currencyCode: 'CAD',
              locale: locale ?? 'en',
            })
          )}
        </>
      ) : isPromotion ? (
        <>{t(`search.facet.promotions.${cleanLabelFori18n}`)}</>
      ) : isClothingSize ? (
        <>
          {label.replace(
            CLOTHING_SIZE_GROUP_DELIMITER,
            locale === 'en' ? ': ' : ' : '
          )}
        </>
      ) : (
        <>{label}</>
      )}
      <button
        type="button"
        className={cn('ais-CurrentRefinements-delete', classNames?.delete)}
        onClick={(event) => {
          if (isModifierClick(event)) {
            return;
          }

          refine(refinement);
        }}
      >
        <span
          className={cn(styles.deleteIcon, {
            [styles.isProductLine]: isProductLine,
          })}
        >
          ✕
        </span>
      </button>
    </span>
  );
}

function CustomCurrentRefinements(
  props: CurrentRefinementsProps & { locale?: string }
) {
  const { items, refine } = useCurrentRefinements(props);

  const { classNames, locale } = props;

  return items.length ? (
    <ul className={cn('ais-CurrentRefinements-list', classNames?.list)}>
      {items.map((item) => (
        <li
          className={cn('ais-CurrentRefinements-item', classNames?.item)}
          key={[item.indexName, item.label].join('/')}
        >
          <span
            className={cn('ais-CurrentRefinements-label', classNames?.label)}
          >
            {item.label}:
          </span>
          {item.refinements.map((refinement) => (
            <span
              className={cn(
                'ais-CurrentRefinements-category',
                classNames?.category,
                {
                  [styles.isProductLine]:
                    refinement.attribute === AlgoliaFacets.Collection,
                }
              )}
              key={refinement.label}
            >
              <RefinementLabel
                refinement={refinement}
                classNames={classNames}
                refine={refine}
                locale={locale}
              />
            </span>
          ))}
        </li>
      ))}
    </ul>
  ) : null;
}

export default CustomCurrentRefinements;
